import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Package, MapPin, CheckCircle, XCircle } from 'lucide-react';

interface BulkOperationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyOperation: (operation: 'enable' | 'disable', scope: 'selected' | 'all') => void;
  selectedItemCount: number;
  selectedSiteCount: number;
  totalItemCount: number;
  totalSiteCount: number;
}

export default function BulkOperationsDialog({
  open,
  onOpenChange,
  onApplyOperation,
  selectedItemCount,
  selectedSiteCount,
  totalItemCount,
  totalSiteCount,
}: BulkOperationsDialogProps) {
  const [operation, setOperation] = useState<'enable' | 'disable'>('enable');
  const [scope, setScope] = useState<'selected' | 'all'>('selected');

  const handleApply = () => {
    onApplyOperation(operation, scope);
    onOpenChange(false);
  };

  const hasSelection = selectedItemCount > 0 || selectedSiteCount > 0;
  
  // Calculate affected combinations
  const affectedItems = scope === 'selected' && selectedItemCount > 0 
    ? selectedItemCount 
    : totalItemCount;
  const affectedSites = scope === 'selected' && selectedSiteCount > 0
    ? selectedSiteCount
    : totalSiteCount;
  const affectedCombinations = affectedItems * affectedSites;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Bulk Operations</DialogTitle>
          <DialogDescription>
            Apply availability changes to multiple item-site combinations at once.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-3">
            <Label className="text-base font-medium">Operation</Label>
            <RadioGroup value={operation} onValueChange={(value: 'enable' | 'disable') => setOperation(value)}>
              <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50">
                <RadioGroupItem value="enable" id="enable" />
                <Label htmlFor="enable" className="flex items-center gap-2 cursor-pointer flex-1">
                  <CheckCircle className="h-4 w-4 text-success" />
                  <span>Enable Availability</span>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50">
                <RadioGroupItem value="disable" id="disable" />
                <Label htmlFor="disable" className="flex items-center gap-2 cursor-pointer flex-1">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <span>Disable Availability</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label className="text-base font-medium">Apply To</Label>
            <RadioGroup value={scope} onValueChange={(value: 'selected' | 'all') => setScope(value)}>
              <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50">
                <RadioGroupItem value="selected" id="selected" disabled={!hasSelection} />
                <Label htmlFor="selected" className="cursor-pointer flex-1">
                  <div className="space-y-1">
                    <div className="font-medium">Selected Items & Sites</div>
                    <div className="text-sm text-muted-foreground">
                      {selectedItemCount > 0 && selectedSiteCount > 0 ? (
                        <>
                          <span className="inline-flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {selectedItemCount} items
                          </span>
                          {' × '}
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {selectedSiteCount} sites
                          </span>
                          {' = '}
                          <span className="font-medium">
                            {selectedItemCount * selectedSiteCount} combinations
                          </span>
                        </>
                      ) : selectedItemCount > 0 ? (
                        <>
                          <span className="inline-flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {selectedItemCount} items
                          </span>
                          {' × '}
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            All {totalSiteCount} sites
                          </span>
                          {' = '}
                          <span className="font-medium">
                            {selectedItemCount * totalSiteCount} combinations
                          </span>
                        </>
                      ) : selectedSiteCount > 0 ? (
                        <>
                          <span className="inline-flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            All {totalItemCount} items
                          </span>
                          {' × '}
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {selectedSiteCount} sites
                          </span>
                          {' = '}
                          <span className="font-medium">
                            {totalItemCount * selectedSiteCount} combinations
                          </span>
                        </>
                      ) : (
                        'No items or sites selected'
                      )}
                    </div>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all" className="cursor-pointer flex-1">
                  <div className="space-y-1">
                    <div className="font-medium">All Items & Sites</div>
                    <div className="text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Package className="h-3 w-3" />
                        {totalItemCount} items
                      </span>
                      {' × '}
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {totalSiteCount} sites
                      </span>
                      {' = '}
                      <span className="font-medium">
                        {totalItemCount * totalSiteCount} combinations
                      </span>
                    </div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="rounded-lg bg-muted p-3">
            <p className="text-sm text-muted-foreground">
              This will {operation === 'enable' ? 'enable' : 'disable'} availability for{' '}
              <span className="font-medium text-foreground">
                {affectedCombinations} item-site combinations
              </span>.
              Changes will be marked as pending until you save.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleApply}
            disabled={scope === 'selected' && !hasSelection}
          >
            Apply {operation === 'enable' ? 'Enable' : 'Disable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}